/**
 * DXF-JSON loading helpers.
 *
 * The external DXF parser returns a JSON document (see DxfJsonDocument). That
 * document can reach the app two ways:
 *   1. As a file the user uploads (handled in the Toolbar via parseDxfJson).
 *   2. As an API response fetched from a backend endpoint (this module).
 *
 * Both paths funnel through parseDxfJson() so rendering is identical.
 */
import axios, { AxiosRequestConfig } from 'axios';
import { parseDxfJson, ParsedFloorPlan, DxfJsonDocument } from './dxf';
import { getFloorPlansApiBase, getImportsApiUrl } from './apiConfig';
import { ensureToken } from './authToken';
import { http } from './http';
import type { LayoutProject } from '../types';
import type { EquipmentInstance } from '../features/equipment/equipmentTypes';
import { dxfParseWorkerClient } from '../workers/dxfParseWorkerClient';

export interface DxfJsonApiResult extends ParsedFloorPlan {
  /** Original file/document name, useful for labelling the floor-plan layer. */
  name: string;
  /** The raw parsed document, in case callers need layer/metadata info. */
  document: DxfJsonDocument;
  /** Optional pre-extracted equipment (worker path) so the UI can skip a second document walk. */
  extractedEquipment?: EquipmentInstance[];
}

export interface BackendParserDocument {
  entities?: unknown[];
  blocks?: Array<{ name?: string; x?: number; y?: number; entities?: unknown[] }>;
  tables?: {
    layers?:
      | { layers?: Array<{ name?: string; color?: number; visible?: boolean; flags?: number }> }
      | Array<{ name?: string; color?: number; visible?: boolean; flags?: number }>
      | Record<string, unknown>;
  };
  header?: Record<string, unknown>;
}

export interface BackendImportApiResult extends ParsedFloorPlan {
  name: string;
  document: BackendParserDocument;
}

export interface BackendImportUploadResult {
  job_id?: string;
  floor_plan_id: string;
  status?: string;
  message?: string;
}

export interface ImportJobStatus {
  id: string;
  floor_plan_id: string;
  status: string;
  progress: number | null;
  error_message: string | null;
  /** Pipeline stage the server is running, when it reports one. */
  stage: string | null;
  /** Server's description of that stage. */
  message: string | null;
  /** True once the lossless document can be fetched without probing. */
  lossless_ready: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface BackendFloorPlanListItem {
  floor_plan_id: string;
  name: string;
  created_at: string | null;
  updated_at: string | null;
  /** When the current user last opened it; null if never. */
  opened_at: string | null;
  block: string | null;
  layer: string | null;
  category: string | null;
}

/** Lighter CRUD / list endpoints only. Heavy layout loads use no client timeout. */
const DEFAULT_API_TIMEOUT_MS = 30000;
/** Axios `0` = wait indefinitely (heavy DXF / layout payloads). */
const NO_TIMEOUT_MS = 0;
const HEAVY_LOAD_TIMEOUT_RETRIES = 2;
const HEAVY_LOAD_RETRY_DELAY_MS = 2500;
/** Job-status polling after a successful POST /imports (202 + job_id). */
const IMPORT_JOB_POLL_INITIAL_MS = 2000;
// The server now reports a stage every few seconds; backing off to 15 s left
// the overlay stale and added up to a quarter minute of idle wait on completion.
const IMPORT_JOB_POLL_MAX_MS = 4000;
const IMPORT_JOB_POLL_MAX_WAIT_MS = 12 * 60_000;
/**
 * Fallback only: after a dropped import response with no job_id, poll the
 * floor-plan list for the completed project. Exponential backoff, ~12 min budget.
 */
const IMPORT_RECOVER_INITIAL_MS = 5000;
const IMPORT_RECOVER_MAX_MS = 25_000;
const IMPORT_RECOVER_MAX_WAIT_MS = 12 * 60_000;
const floorPlanListRequests = new Map<string, Promise<BackendFloorPlanListItem[]>>();

const IMPORT_JOB_SUCCESS_STATUSES = new Set([
  'completed',
  'complete',
  'succeeded',
  'success',
  'done',
  'ready',
]);
const IMPORT_JOB_FAILURE_STATUSES = new Set([
  'failed',
  'failure',
  'error',
  'cancelled',
  'canceled',
]);

function readTimeoutFromEnv(key: 'VITE_API_TIMEOUT_MS'): number | null {
  const raw = import.meta.env[key];
  if (typeof raw !== 'string') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function resolveApiTimeoutMs(override?: number): number {
  return override ?? readTimeoutFromEnv('VITE_API_TIMEOUT_MS') ?? DEFAULT_API_TIMEOUT_MS;
}

function isTimeoutError(err: unknown): boolean {
  if (!axios.isAxiosError(err)) {
    return err instanceof Error && /timeout/i.test(err.message);
  }
  return err.code === 'ECONNABORTED' || /timeout/i.test(err.message);
}

/**
 * Proxy / gateway layers often close long DXF uploads as a generic Network Error
 * or HTTP 504 while the API keeps processing. Treat those as recoverable for
 * heavy loads (recover-by-name for POST; retry for GET).
 */
export function isTransientHeavyLoadError(err: unknown): boolean {
  if (isTimeoutError(err)) return true;
  if (!axios.isAxiosError(err)) {
    return (
      err instanceof Error &&
      /network error|failed to fetch|econnreset|econnrefused|connection interrupted|gateway timeout|504/i.test(
        err.message
      )
    );
  }
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNRESET' || err.code === 'ECONNABORTED') {
    return true;
  }
  // ALB / nginx idle timeouts return 502/503/504 with a body — backend may still finish.
  const status = err.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return true;
  }
  // No HTTP response usually means the connection was closed mid-flight.
  return !err.response && /network error|timeout|connection interrupted/i.test(err.message || '');
}

/** True when the thrown import error is the recoverable “connection dropped” case. */
export function isRecoverableImportInterruptMessage(message: string): boolean {
  return /connection interrupted|still processing a large layout|upload may have completed|gateway timeout|status=504|status=502|status=503/i.test(
    message
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Heavy layout GET requests: no client timeout. If a proxy/network layer still
 * surfaces a timeout / Network Error, retry instead of failing immediately.
 * Do not use for POST imports — retries can create duplicate floor plans.
 */
async function withHeavyLoadRetry<T>(label: string, run: () => Promise<T>): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= HEAVY_LOAD_TIMEOUT_RETRIES; attempt++) {
    try {
      return await run();
    } catch (err) {
      lastErr = err;
      if (!isTransientHeavyLoadError(err) || attempt === HEAVY_LOAD_TIMEOUT_RETRIES) break;
      await sleep(HEAVY_LOAD_RETRY_DELAY_MS * (attempt + 1));
    }
  }

  if (isTransientHeavyLoadError(lastErr)) {
    throw new Error(
      `${label} is still preparing a large layout. Please wait a moment and try again.`,
      { cause: lastErr }
    );
  }
  if (lastErr instanceof Error) throw lastErr;
  throw new Error(String(lastErr));
}

function toErrorMessage(err: unknown): string {
  if (isTransientHeavyLoadError(err)) {
    return 'Connection interrupted while the backend was still processing a large layout. The upload may have completed — refresh and open the floor plan, or try again.';
  }
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const statusText = err.response?.statusText;
    const responseData = err.response?.data;
    const responseText =
      typeof responseData === 'string'
        ? responseData
        : responseData != null
          ? JSON.stringify(responseData)
          : '';
    const parts = [
      err.message,
      status ? `status=${status}` : null,
      statusText || null,
      responseText ? `response=${responseText}` : null,
    ].filter(Boolean);
    return parts.join(' | ');
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

function asUploadResult(payload: unknown): BackendImportUploadResult | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = payload as Record<string, unknown>;
  const floorPlanIdRaw = raw['floor_plan_id'] ?? raw['floorPlanId'];
  const floorPlanId = typeof floorPlanIdRaw === 'string' ? floorPlanIdRaw.trim() : '';
  if (!floorPlanId) return null;

  const jobIdRaw = raw['job_id'] ?? raw['jobId'];
  return {
    floor_plan_id: floorPlanId,
    job_id: typeof jobIdRaw === 'string' && jobIdRaw.trim() ? jobIdRaw.trim() : undefined,
    status: typeof raw['status'] === 'string' ? raw['status'] : undefined,
    message: typeof raw['message'] === 'string' ? raw['message'] : undefined,
  };
}

function normalizeImportJobStatus(status: string): string {
  return status.trim().toLowerCase();
}

export function isImportJobSuccessStatus(status: string): boolean {
  return IMPORT_JOB_SUCCESS_STATUSES.has(normalizeImportJobStatus(status));
}

export function isImportJobFailureStatus(status: string): boolean {
  return IMPORT_JOB_FAILURE_STATUSES.has(normalizeImportJobStatus(status));
}

export function asImportJobStatus(payload: unknown): ImportJobStatus | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = payload as Record<string, unknown>;
  const idRaw = raw['id'] ?? raw['job_id'] ?? raw['jobId'];
  const floorPlanIdRaw = raw['floor_plan_id'] ?? raw['floorPlanId'];
  const statusRaw = raw['status'];
  const id = typeof idRaw === 'string' ? idRaw.trim() : '';
  const floorPlanId = typeof floorPlanIdRaw === 'string' ? floorPlanIdRaw.trim() : '';
  const status = typeof statusRaw === 'string' ? statusRaw.trim() : '';
  if (!id || !floorPlanId || !status) return null;

  const progressRaw = raw['progress'];
  const rawProgressNum =
    typeof progressRaw === 'number'
      ? progressRaw
      : typeof progressRaw === 'string' && progressRaw.trim() !== ''
        ? Number(progressRaw)
        : null;
  const progress =
    rawProgressNum !== null && Number.isFinite(rawProgressNum) ? rawProgressNum : null;
  const errorRaw = raw['error_message'] ?? raw['errorMessage'];
  const createdRaw = raw['created_at'] ?? raw['createdAt'];
  const updatedRaw = raw['updated_at'] ?? raw['updatedAt'];
  const stageRaw = raw['stage'];
  const messageRaw = raw['message'];
  const readyRaw = raw['lossless_ready'] ?? raw['losslessReady'];

  return {
    id,
    floor_plan_id: floorPlanId,
    status,
    progress,
    error_message: typeof errorRaw === 'string' && errorRaw.trim() ? errorRaw.trim() : null,
    stage: typeof stageRaw === 'string' && stageRaw.trim() ? stageRaw.trim() : null,
    message: typeof messageRaw === 'string' && messageRaw.trim() ? messageRaw.trim() : null,
    lossless_ready: readyRaw === true,
    created_at: typeof createdRaw === 'string' ? createdRaw : null,
    updated_at: typeof updatedRaw === 'string' ? updatedRaw : null,
  };
}

export function normalizeApiProgress(raw: number | null | undefined): number | null {
  if (raw == null || !Number.isFinite(raw)) return null;
  if (raw < 0) return 0;
  if (raw > 0 && raw <= 1) {
    return Math.round(raw * 100);
  }
  return Math.min(100, Math.round(raw));
}

function formatImportJobProgress(progress: number | null): string {
  const pct = normalizeApiProgress(progress);
  return pct !== null ? ` (${pct}%)` : '';
}

function nextBackoffMs(previousMs: number, initialMs: number, maxMs: number): number {
  if (previousMs <= 0) return initialMs;
  return Math.min(maxMs, Math.round(previousMs * 2));
}

/**
 * Normalizes API date values into valid ISO-8601 strings.
 * Handles:
 * - Naive ISO strings from Python/SQL without 'Z' (e.g. '2026-10-07T09:50:20.123456' -> '2026-10-07T09:50:20.123456Z')
 * - SQL datetime strings with spaces (e.g. '2026-10-07 09:50:20' -> '2026-10-07T09:50:20Z')
 * - Epoch numeric timestamps in seconds or milliseconds (e.g. 1728287345 or 1728287345000)
 * - Date instances
 * - Trailing timezones or offsets ('Z', '+00:00', '-05:00', etc.)
 */
export function normalizeDateString(value: unknown): string | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return null;
    const ms = value < 1e11 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }

  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
    return null;
  }

  // Check if string contains only digits (or decimal), e.g. "1728287345" or "1728287345000"
  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    const num = Number(trimmed);
    if (Number.isFinite(num) && num > 0) {
      const ms = num < 1e11 ? num * 1000 : num;
      const date = new Date(ms);
      return Number.isNaN(date.getTime()) ? null : date.toISOString();
    }
  }

  // Format SQL space-separated datetime to ISO: '2026-10-07 09:50:20' -> '2026-10-07T09:50:20'
  let iso = trimmed.includes(' ') && !trimmed.includes('T') ? trimmed.replace(' ', 'T') : trimmed;

  // If there's no timezone offset (no 'Z', no '+HH:MM', no '-HH:MM' at the end),
  // backend timestamps are in UTC, so append 'Z' so browsers parse as UTC rather than local time.
  const hasTimezone = /([zZ]|[+-]\d{2}(:?\d{2})?)$/.test(iso);
  if (!hasTimezone) {
    iso = `${iso}Z`;
  }

  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    const fallback = new Date(trimmed);
    if (!Number.isNaN(fallback.getTime())) {
      return fallback.toISOString();
    }
    return null;
  }

  return parsed.toISOString();
}

export function asFloorPlanListItem(payload: unknown): BackendFloorPlanListItem | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = payload as Record<string, unknown>;
  const rawMetaCandidates = [raw['metadata'], raw['meta'], raw['details']];
  const rawMeta = rawMetaCandidates.find(
    (candidate) => candidate && typeof candidate === 'object'
  ) as Record<string, unknown> | undefined;

  const pickStringFrom = (
    source: Record<string, unknown> | undefined,
    ...keys: string[]
  ): string | null => {
    if (!source) return null;
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    return null;
  };

  const pickString = (...keys: string[]): string | null =>
    pickStringFrom(raw, ...keys) ?? pickStringFrom(rawMeta, ...keys);

  const dateContainers: (Record<string, unknown> | undefined)[] = [
    raw,
    rawMeta,
    raw['summary'] && typeof raw['summary'] === 'object'
      ? (raw['summary'] as Record<string, unknown>)
      : undefined,
    raw['stats'] && typeof raw['stats'] === 'object'
      ? (raw['stats'] as Record<string, unknown>)
      : undefined,
    raw['activity'] && typeof raw['activity'] === 'object'
      ? (raw['activity'] as Record<string, unknown>)
      : undefined,
    raw['user_activity'] && typeof raw['user_activity'] === 'object'
      ? (raw['user_activity'] as Record<string, unknown>)
      : undefined,
  ];

  const pickDate = (...keys: string[]): string | null => {
    for (const container of dateContainers) {
      if (!container) continue;
      for (const key of keys) {
        const val = container[key];
        const normalized = normalizeDateString(val);
        if (normalized) return normalized;
      }
    }
    return null;
  };

  const rawId = raw['floor_plan_id'] ?? raw['floorPlanId'] ?? raw['id'];
  const floorPlanId =
    typeof rawId === 'string' ? rawId.trim() : typeof rawId === 'number' ? String(rawId) : '';
  if (!floorPlanId) return null;

  const rawName = raw['name'] ?? raw['file_name'] ?? raw['title'] ?? raw['project_name'];
  const name =
    typeof rawName === 'string' && rawName.trim().length > 0
      ? rawName.trim()
      : `Floor Plan ${floorPlanId}`;

  const createdAt = pickDate('created_at', 'createdAt', 'uploaded_at', 'uploadedAt');
  const updatedAt = pickDate('updated_at', 'updatedAt');
  const openedAt = pickDate(
    'opened_at',
    'openedAt',
    'last_opened_at',
    'lastOpenedAt',
    'last_opened',
    'lastOpened',
    'last_open',
    'lastOpen',
    'last_open_at',
    'lastOpenAt',
    'opened_time',
    'openedTime',
    'last_accessed_at',
    'last_accessed',
    'lastAccessed'
  );

  const block = pickString('block', 'block_name', 'blockName', 'block_type', 'blockType');
  const layer = pickString('layer', 'layer_name', 'layerName', 'layer_type', 'layerType');
  const category = pickString(
    'category',
    'plan_category',
    'planCategory',
    'item_category',
    'itemCategory',
    'type'
  );

  return {
    floor_plan_id: floorPlanId,
    name,
    created_at: createdAt,
    updated_at: updatedAt,
    opened_at: openedAt,
    block,
    layer,
    category,
  };
}

function asFloorPlanList(payload: unknown): BackendFloorPlanListItem[] {
  const envelope =
    payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
  const listCandidates: unknown[] = [];

  if (Array.isArray(payload)) listCandidates.push(payload);
  if (envelope) {
    listCandidates.push(
      envelope['items'],
      envelope['floor_plans'],
      envelope['floorPlans'],
      envelope['summaries'],
      envelope['plans'],
      envelope['floor_plan_summaries'],
      envelope['data'],
      envelope['results'],
      envelope['payload']
    );
  }

  const list = listCandidates.find((candidate) => Array.isArray(candidate)) as
    | unknown[]
    | undefined;
  if (!list) return [];

  return list
    .map(asFloorPlanListItem)
    .filter((item): item is BackendFloorPlanListItem => item !== null);
}

/** Picks a human-friendly name from the document, falling back gracefully. */
function resolveName(doc: DxfJsonDocument, fallback: string): string {
  return doc.file_name || doc.name || fallback;
}

/**
 * Converts an already-parsed API response object into a renderable floor plan.
 * Use this when you have the JSON in hand (e.g. from your own fetch/axios call,
 * a websocket message, or a cached response).
 */
export function loadDxfJsonDocument(
  doc: DxfJsonDocument,
  fallbackName = 'Imported DXF'
): DxfJsonApiResult {
  const parsed = parseDxfJson(doc);
  return { ...parsed, name: resolveName(doc, fallbackName), document: doc };
}

/**
 * Fetches a DXF-JSON document from an API endpoint and returns a renderable
 * floor plan. The endpoint is expected to respond with a DxfJsonDocument body.
 *
 * @example
 *   const result = await fetchDxfJsonLayout('/api/dxf/123');
 *   dispatch(setFloorPlan({ name: result.name, polylines: result.polylines,
 *                           bounds: result.bounds, scaleFactor: result.scaleFactor }));
 */
export async function fetchDxfJsonLayout(
  url: string,
  init?: RequestInit
): Promise<DxfJsonApiResult> {
  const config: AxiosRequestConfig = {
    url,
    method: (init?.method as AxiosRequestConfig['method']) || 'GET',
    headers: init?.headers as AxiosRequestConfig['headers'],
    data: init?.body,
  };

  let res;
  try {
    res = await withHeavyLoadRetry(`DXF layout (${url})`, () =>
      http.request<DxfJsonDocument>({
        timeout: NO_TIMEOUT_MS,
        ...config,
      })
    );
  } catch (err) {
    throw new Error(`DXF API request failed for ${url}: ${toErrorMessage(err)}`, {
      cause: err,
    });
  }
  const doc = res.data;
  if (!doc || !Array.isArray(doc.entities)) {
    throw new Error('DXF API response is not a valid DXF-JSON document (missing "entities").');
  }
  return loadDxfJsonDocument(doc, url.split('/').pop() || 'Imported DXF');
}

export type ImportStatusCallback = (
  message: string,
  progress?: number | null,
  stage?: 'upload' | 'processing'
) => void;

/**
 * Fetches import job status.
 * GET /api/v1/imports/{job_id}
 */
export async function fetchImportJob(params: {
  jobId: string;
  baseUrl?: string;
  timeoutMs?: number;
}): Promise<ImportJobStatus> {
  const jobId = params.jobId.trim();
  if (!jobId) throw new Error('jobId is required to fetch import job status.');

  const baseUrl = (params.baseUrl || getImportsApiUrl()).replace(/\/$/, '');
  const url = `${baseUrl}/${encodeURIComponent(jobId)}`;
  const timeoutMs = resolveApiTimeoutMs(params.timeoutMs);

  try {
    const res = await http.get(url, { timeout: timeoutMs });
    const job = asImportJobStatus(res.data);
    if (!job) {
      throw new Error('Import job response is missing required fields.');
    }
    return job;
  } catch (err) {
    throw new Error(`Import job status request failed for ${url}: ${toErrorMessage(err)}`, {
      cause: err,
    });
  }
}

/**
 * Polls GET /imports/{job_id} until the job reaches a terminal status.
 */
export async function waitForImportJob(params: {
  jobId: string;
  floorPlanId?: string;
  baseUrl?: string;
  onStatus?: ImportStatusCallback;
  initialDelayMs?: number;
  maxDelayMs?: number;
  maxWaitMs?: number;
}): Promise<BackendImportUploadResult> {
  const jobId = params.jobId.trim();
  if (!jobId) throw new Error('jobId is required to wait for an import job.');

  const startedAt = Date.now();
  const maxWaitMs = params.maxWaitMs ?? IMPORT_JOB_POLL_MAX_WAIT_MS;
  const initialDelayMs = params.initialDelayMs ?? IMPORT_JOB_POLL_INITIAL_MS;
  const maxDelayMs = params.maxDelayMs ?? IMPORT_JOB_POLL_MAX_MS;
  let delayMs = 0;
  let attempt = 0;

  while (Date.now() - startedAt < maxWaitMs) {
    if (delayMs > 0) {
      await sleep(delayMs);
    }

    attempt += 1;
    try {
      const job = await fetchImportJob({
        jobId,
        baseUrl: params.baseUrl,
      });

      if (isImportJobFailureStatus(job.status)) {
        throw new Error(
          job.error_message
            ? `Import job failed: ${job.error_message}`
            : `Import job failed with status “${job.status}”.`
        );
      }

      if (isImportJobSuccessStatus(job.status)) {
        const floorPlanId = job.floor_plan_id || params.floorPlanId || '';
        if (!floorPlanId) {
          throw new Error('Import job completed but floor_plan_id is missing.');
        }
        params.onStatus?.('Import finished on the server — loading geometry…', 100, 'processing');
        return {
          job_id: job.id,
          floor_plan_id: floorPlanId,
          status: job.status,
          message: job.error_message ?? undefined,
        };
      }

      const apiPct = normalizeApiProgress(job.progress);
      const progressText = formatImportJobProgress(job.progress);
      const statusLabel = job.status.trim();
      const displayStatus = statusLabel
        ? statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)
        : 'Processing';
      const statusDetail =
        displayStatus.toLowerCase() === 'processing' ? '' : ` (${displayStatus.toLowerCase()})`;
      // Prefer the server's own description of the stage it is running.
      const stageText = job.message ?? job.stage;
      params.onStatus?.(
        stageText
          ? `${stageText}${progressText}…`
          : attempt === 1
            ? `Import accepted — ${displayStatus.toLowerCase()} on server${progressText}…`
            : `Processing on server${statusDetail}${progressText}…`,
        apiPct,
        'processing'
      );
    } catch (err) {
      // Terminal job failure — stop immediately.
      if (err instanceof Error && /Import job failed/i.test(err.message)) throw err;
      if (err instanceof Error && /floor_plan_id is missing/i.test(err.message)) throw err;

      // Transient network / 5xx while polling should not abort a long job.
      if (
        isTransientHeavyLoadError(err) ||
        (err instanceof Error && /status=5\d\d/.test(err.message))
      ) {
        params.onStatus?.(
          `Connection hiccup while checking import status — retrying (${attempt})…`,
          null,
          'processing'
        );
      } else {
        params.onStatus?.(`Waiting for import job status (retry ${attempt})…`, null, 'processing');
      }
    }

    delayMs = nextBackoffMs(delayMs, initialDelayMs, maxDelayMs);
  }

  throw new Error(
    `Import job ${jobId} did not finish within ${Math.round(maxWaitMs / 60000)} minutes. Try Keep waiting, or open the project from the list if it appears.`
  );
}

/**
 * When the import HTTP response is dropped (proxy Network Error / timeout) before
 * a job_id is returned, find the newly stored floor plan by project name.
 * Uses exponential backoff — not a fixed 5s spam loop.
 */
export async function recoverImportByProjectName(
  projectName: string,
  startedAtMs: number,
  onStatus?: ImportStatusCallback
): Promise<BackendImportUploadResult | null> {
  const want = projectName.trim().toLowerCase();
  if (!want) return null;

  const recoverStartedAt = Date.now();
  let delayMs = 0;
  let attempt = 0;

  while (Date.now() - recoverStartedAt < IMPORT_RECOVER_MAX_WAIT_MS) {
    attempt += 1;
    onStatus?.(
      attempt === 1
        ? 'Upload connection dropped — waiting for the server to finish…'
        : `Still waiting for “${projectName}” on the server (check ${attempt})…`,
      null,
      'processing'
    );

    delayMs = nextBackoffMs(delayMs, IMPORT_RECOVER_INITIAL_MS, IMPORT_RECOVER_MAX_MS);
    await sleep(delayMs);

    let plans: BackendFloorPlanListItem[];
    try {
      plans = await fetchBackendFloorPlans();
    } catch {
      continue;
    }

    const matches = plans
      .filter((plan) => plan.name.trim().toLowerCase() === want)
      .sort((a, b) => {
        const aTime = a.created_at ? Date.parse(a.created_at) : 0;
        const bTime = b.created_at ? Date.parse(b.created_at) : 0;
        return bTime - aTime;
      });

    const recent = matches.find((plan) => {
      if (!plan.created_at) return true;
      const created = Date.parse(plan.created_at);
      if (!Number.isFinite(created)) return true;
      // Allow clock skew: accept plans created shortly before upload started.
      return created >= startedAtMs - 60_000;
    });

    if (recent) {
      onStatus?.('Found the uploaded floor plan — loading geometry…', 100, 'processing');
      return {
        floor_plan_id: recent.floor_plan_id,
        status: 'recovered',
        message: 'Import response was interrupted; recovered completed floor plan from list.',
      };
    }
  }

  return null;
}

/**
 * Uploads a raw DXF file to a backend parser endpoint and returns the import
 * metadata payload (including floor_plan_id) used to fetch lossless JSON.
 *
 * Preferred path: POST returns job_id → poll GET /imports/{job_id}.
 * Fallback on 504/connection drop without job_id: recover via floor-plan summary.
 * We do **not** re-POST on failure (that would create duplicates).
 */
export async function importBackendDxfLayout(params: {
  file: File;
  projectName: string;
  url?: string;
  timeoutMs?: number;
  onStatus?: ImportStatusCallback;
}): Promise<BackendImportUploadResult> {
  const { file, projectName, onStatus } = params;
  const baseUrl = params.url || getImportsApiUrl();
  // Heavy upload/parse — no client timeout unless explicitly overridden.
  const timeoutMs = params.timeoutMs ?? NO_TIMEOUT_MS;

  // API contract: `project_name` is a query param; `file` is the multipart body.
  // Avoid `new URL(relative)` — relative bases like `/api/v1/imports` throw Invalid URL.
  const separator = baseUrl.includes('?') ? '&' : '?';
  const requestUrl = `${baseUrl}${separator}project_name=${encodeURIComponent(projectName)}`;

  const formData = new FormData();
  formData.append('file', file);
  const startedAtMs = Date.now();

  let res;
  try {
    onStatus?.(`Uploading “${file.name}”…`, 0, 'upload');
    // Single POST only — do not retry uploads (duplicate floor plans).
    res = await http.post(requestUrl, formData, {
      timeout: timeoutMs,
    });
  } catch (err) {
    // No job_id available — fall back to summary recovery only.
    if (isTransientHeavyLoadError(err)) {
      const recovered = await recoverImportByProjectName(projectName, startedAtMs, onStatus);
      if (recovered) return recovered;
    }
    throw new Error(`Backend import request failed for ${requestUrl}: ${toErrorMessage(err)}`, {
      cause: err,
    });
  }
  const payload = res.data as unknown;
  const uploadResult = asUploadResult(payload);
  if (!uploadResult) {
    throw new Error('Backend import API response is not valid (missing "floor_plan_id").');
  }

  if (uploadResult.status && isImportJobSuccessStatus(uploadResult.status)) {
    onStatus?.('Upload completed — processing geometry…', 100, 'upload');
    return uploadResult;
  }

  if (uploadResult.job_id) {
    onStatus?.('Upload completed — processing on server…', 0, 'processing');
    const importsBase = baseUrl.replace(/\?.*$/, '').replace(/\/$/, '');
    return waitForImportJob({
      jobId: uploadResult.job_id,
      floorPlanId: uploadResult.floor_plan_id,
      baseUrl: importsBase,
      onStatus,
    });
  }

  return uploadResult;
}

/**
 * Fetches the backend-generated lossless JSON by floor plan id and converts it
 * to the same ParsedFloorPlan format used by DXF-JSON import.
 *
 * Download uses `responseType: 'text'` so JSON.parse + DXF conversion can run
 * in a Web Worker instead of freezing the UI for minutes on large fabs.
 */
export async function fetchBackendLosslessJsonLayout(params: {
  floorPlanId: string;
  baseUrl?: string;
  fallbackName?: string;
  timeoutMs?: number;
  onStatus?: (message: string, progress?: number | null) => void;
}): Promise<DxfJsonApiResult> {
  const floorPlanId = params.floorPlanId.trim();
  if (!floorPlanId) {
    throw new Error('floorPlanId is required to fetch backend lossless JSON.');
  }

  const baseUrl = (params.baseUrl || getFloorPlansApiBase()).replace(/\/$/, '');
  const url = `${baseUrl}/floor-plans/${encodeURIComponent(floorPlanId)}/lossless-json?embed_categories=true`;
  // Heavy layout payload — wait as long as the backend needs unless overridden.
  const timeoutMs = params.timeoutMs ?? NO_TIMEOUT_MS;
  const fallbackName = params.fallbackName || `Floor Plan ${floorPlanId}`;
  const onStatus = params.onStatus;

  let res;
  try {
    res = await withHeavyLoadRetry(`Floor plan ${floorPlanId}`, () =>
      http.get<ArrayBuffer>(url, {
        timeout: timeoutMs,
        responseType: 'arraybuffer',
        // Keep the raw bytes — do not JSON.parse on the UI thread.
        transformResponse: [(data) => data],
        onDownloadProgress: (event) => {
          if (!onStatus) return;
          const total = event.total ?? 0;
          if (total > 0 && Number.isFinite(event.loaded)) {
            const pct = Math.min(100, Math.round((event.loaded / total) * 100));
            onStatus('Downloading floor plan…', pct);
            return;
          }
          if (Number.isFinite(event.loaded) && event.loaded > 0) {
            onStatus('Downloading floor plan…');
          }
        },
      })
    );
  } catch (err) {
    throw new Error(
      `Backend floor-plan lossless JSON request failed for ${url}: ${toErrorMessage(err)}`,
      { cause: err }
    );
  }

  const buffer = res.data;
  if (!(buffer instanceof ArrayBuffer) || buffer.byteLength === 0) {
    throw new Error('Backend lossless JSON response was empty.');
  }

  onStatus?.('Parsing layers and polylines…', 100);
  const parsed = await dxfParseWorkerClient.parseLosslessJsonBuffer(buffer, fallbackName);

  return {
    ...parsed,
    name: parsed.name,
    document: parsed.document,
    extractedEquipment: parsed.extractedEquipment,
  };
}

/**
 * Fetches previously uploaded floor plans used by the landing screen grid.
 */
export function fetchBackendFloorPlans(params?: {
  baseUrl?: string;
  url?: string;
  timeoutMs?: number;
}): Promise<BackendFloorPlanListItem[]> {
  const baseUrl = (params?.baseUrl || getFloorPlansApiBase()).replace(/\/$/, '');
  const url = params?.url || `${baseUrl}/floor-plans/summary`;
  const timeoutMs = resolveApiTimeoutMs(params?.timeoutMs);

  const requestKey = `${url}::${timeoutMs}`;
  const pendingRequest = floorPlanListRequests.get(requestKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = http
    .get(url, {
      timeout: timeoutMs,
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    })
    .then((res) => asFloorPlanList(res.data as unknown))
    .catch((err) => {
      throw new Error(`Backend floor-plan list request failed for ${url}: ${toErrorMessage(err)}`, {
        cause: err,
      });
    })
    .finally(() => {
      floorPlanListRequests.delete(requestKey);
    });

  floorPlanListRequests.set(requestKey, request);
  return request;
}

/**
 * Deletes a floor plan by id.
 * DELETE /api/v1/floor-plans/{floor_plan_id}
 */
export async function deleteBackendFloorPlan(params: {
  floorPlanId: string;
  baseUrl?: string;
  timeoutMs?: number;
}): Promise<void> {
  const floorPlanId = params.floorPlanId.trim();
  if (!floorPlanId) {
    throw new Error('floorPlanId is required to delete a backend floor plan.');
  }

  const baseUrl = (params.baseUrl || getFloorPlansApiBase()).replace(/\/$/, '');
  const url = `${baseUrl}/floor-plans/${encodeURIComponent(floorPlanId)}`;
  const timeoutMs = resolveApiTimeoutMs(params.timeoutMs);

  try {
    await http.delete(url, { timeout: timeoutMs });
  } catch (err) {
    throw new Error(`Backend floor-plan delete failed for ${url}: ${toErrorMessage(err)}`, {
      cause: err,
    });
  }
}

/**
 * Renames a floor plan (display name only) and returns the saved name.
 * PATCH /api/v1/floor-plans/{floor_plan_id}
 * Body: { "name": "New name" }
 */
export async function renameBackendFloorPlan(params: {
  floorPlanId: string;
  name: string;
  baseUrl?: string;
  timeoutMs?: number;
}): Promise<string> {
  const floorPlanId = params.floorPlanId.trim();
  if (!floorPlanId) {
    throw new Error('floorPlanId is required to rename a backend floor plan.');
  }

  const baseUrl = (params.baseUrl || getFloorPlansApiBase()).replace(/\/$/, '');
  const url = `${baseUrl}/floor-plans/${encodeURIComponent(floorPlanId)}`;
  const timeoutMs = resolveApiTimeoutMs(params.timeoutMs);

  try {
    const res = await http.patch<{ name: string }>(
      url,
      { name: params.name },
      { timeout: timeoutMs }
    );
    return res.data.name;
  } catch (err) {
    throw new Error(`Backend floor-plan rename failed for ${url}: ${toErrorMessage(err)}`, {
      cause: err,
    });
  }
}

export type ProcessMachineMapSource = 'regex' | 'llm';

/**
 * Fetches the process → tool-group → machine hierarchy for a floor plan.
 * `source=regex` is rule-based; `source=llm` uses the LLM pipeline.
 */
export async function fetchProcessMachineMap(params: {
  floorPlanId: string;
  source: ProcessMachineMapSource;
  baseUrl?: string;
  timeoutMs?: number;
}): Promise<unknown> {
  const floorPlanId = params.floorPlanId.trim();
  if (!floorPlanId) {
    throw new Error('floorPlanId is required to fetch process-machine map.');
  }

  const baseUrl = (params.baseUrl || getFloorPlansApiBase()).replace(/\/$/, '');
  const url =
    `${baseUrl}/floor-plans/${encodeURIComponent(floorPlanId)}` +
    `/process-machine-map?source=${encodeURIComponent(params.source)}`;
  const timeoutMs = params.timeoutMs ?? NO_TIMEOUT_MS;

  try {
    const res = await withHeavyLoadRetry(`Process-machine map (${floorPlanId})`, () =>
      http.get(url, { timeout: timeoutMs })
    );
    const payload = res.data as unknown;
    // Some backends wrap the tree as { data: {...} } / { result: {...} }.
    if (payload && typeof payload === 'object' && !('root' in (payload as object))) {
      const envelope = payload as Record<string, unknown>;
      const nested = envelope['data'] ?? envelope['result'] ?? envelope['payload'];
      if (nested && typeof nested === 'object' && 'root' in (nested as object)) {
        return nested;
      }
    }
    return payload;
  } catch (err) {
    throw new Error(`Process-machine map request failed for ${url}: ${toErrorMessage(err)}`, {
      cause: err,
    });
  }
}

export interface ImportDxfTrackResponse {
  message?: string;
  layers?: string[];
  stats?: {
    rails?: number;
    curves?: number;
    junctions?: number;
    direct?: number;
    inserts?: number;
    skipped_xref?: number;
    flattened?: number;
  };
  route?: LayoutProject;
}

/**
 * Rebuild the OHT track already drawn on the DXF's track layer(s) using our own track tools.
 * POST /api/v1/floor-plans/{floor_plan_id}/track-from-layer  body { layers?, transform }.
 * `transform` is the floor plan's DXF->canvas transform (state.floorplan.transform) so the rebuilt
 * track overlays the drawing. `layers` omitted -> the backend auto-detects track layers.
 */
export async function importDxfTrack(params: {
  floorPlanId: string;
  layers?: string[];
  transform: { rawMinX: number; rawMinY: number; invScale: number };
  baseUrl?: string;
  timeoutMs?: number;
}): Promise<ImportDxfTrackResponse> {
  if (!params.floorPlanId) throw new Error('A loaded floor plan is required to extract DXF track.');
  const baseUrl = (params.baseUrl || getFloorPlansApiBase()).replace(/\/$/, '');
  const url = `${baseUrl}/floor-plans/${encodeURIComponent(params.floorPlanId)}/track-from-layer`;
  try {
    const res = await http.post<ImportDxfTrackResponse>(
      url,
      {
        layers: params.layers && params.layers.length ? params.layers : null,
        transform: params.transform,
      },
      { timeout: params.timeoutMs ?? 120000 }
    );
    return res.data;
  } catch (err) {
    throw new Error(`DXF track import failed for ${url}: ${toErrorMessage(err)}`, { cause: err });
  }
}

/**
 * Persists which DXF layers should remain visible for a floor plan.
 * PATCH /api/v1/floor-plans/{floor_plan_id}/show-layers-only
 * Body: { "layer_names": ["LAYER_A", "LAYER_B"] }
 */
export async function saveFloorPlanLayerVisibility(params: {
  floorPlanId: string;
  layerNames: string[];
  baseUrl?: string;
  timeoutMs?: number;
}): Promise<void> {
  const floorPlanId = params.floorPlanId.trim();
  if (!floorPlanId) {
    throw new Error('floorPlanId is required to save layer visibility.');
  }

  const baseUrl = (params.baseUrl || getFloorPlansApiBase()).replace(/\/$/, '');
  const url = `${baseUrl}/floor-plans/${encodeURIComponent(floorPlanId)}/show-layers-only`;
  const timeoutMs = resolveApiTimeoutMs(params.timeoutMs);

  try {
    await http.patch(url, { layer_names: params.layerNames }, { timeout: timeoutMs });
  } catch (err) {
    throw new Error(`Layer visibility save failed for ${url}: ${toErrorMessage(err)}`, {
      cause: err,
    });
  }
}

export interface LayoutSummary {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface LayoutListResponse {
  items: LayoutSummary[];
  total: number;
  limit: number;
  offset: number;
}

/** Fetches layout summaries for a given floor plan. */
export async function fetchLayoutsForFloorPlan(params: {
  floorPlanId: string;
  baseUrl?: string;
  timeoutMs?: number;
}): Promise<LayoutListResponse> {
  const floorPlanId = params.floorPlanId.trim();
  if (!floorPlanId) {
    throw new Error('floorPlanId is required to fetch layouts.');
  }

  const baseUrl = (params.baseUrl || getFloorPlansApiBase()).replace(/\/$/, '');
  const url = `${baseUrl}/layouts/by-floor-plan/${encodeURIComponent(floorPlanId)}`;
  const timeoutMs = resolveApiTimeoutMs(params.timeoutMs);

  try {
    const res = await http.get<LayoutListResponse>(url, { timeout: timeoutMs });
    return res.data;
  } catch (err) {
    throw new Error(`Layout list request failed for ${url}: ${toErrorMessage(err)}`, {
      cause: err,
    });
  }
}

/** Fetches the full Layout project JSON by layout ID. */
export async function fetchLayoutDetail(params: {
  layoutId: string;
  baseUrl?: string;
  timeoutMs?: number;
}): Promise<LayoutProject> {
  const layoutId = params.layoutId.trim();
  if (!layoutId) {
    throw new Error('layoutId is required to fetch layout detail.');
  }

  const baseUrl = (params.baseUrl || getFloorPlansApiBase()).replace(/\/$/, '');
  const url = `${baseUrl}/layouts/${encodeURIComponent(layoutId)}`;
  const timeoutMs = params.timeoutMs ?? NO_TIMEOUT_MS;

  try {
    const res = await withHeavyLoadRetry(`Layout ${layoutId}`, () =>
      http.get<LayoutProject>(url, { timeout: timeoutMs })
    );
    return res.data;
  } catch (err) {
    throw new Error(`Layout detail request failed for ${url}: ${toErrorMessage(err)}`, {
      cause: err,
    });
  }
}

/** Saves/creates or updates a layout in the backend database. */
export async function saveLayoutToBackend(params: {
  layoutPayload: any;
  layoutId?: string | null;
  baseUrl?: string;
  timeoutMs?: number;
}): Promise<any> {
  const baseUrl = (params.baseUrl || getFloorPlansApiBase()).replace(/\/$/, '');
  const isUpdate = Boolean(params.layoutId);
  const url = isUpdate
    ? `${baseUrl}/layouts/${encodeURIComponent(params.layoutId!)}`
    : `${baseUrl}/layouts`;
  const timeoutMs = params.timeoutMs ?? NO_TIMEOUT_MS;

  try {
    const res = await withHeavyLoadRetry(`Layout save (${params.layoutId || 'new'})`, () =>
      isUpdate
        ? http.put(url, params.layoutPayload, { timeout: timeoutMs })
        : http.post(url, params.layoutPayload, { timeout: timeoutMs })
    );
    return res.data;
  } catch (err) {
    throw new Error(`Layout save request failed for ${url}: ${toErrorMessage(err)}`, {
      cause: err,
    });
  }
}

export interface GenerateRouteResponse {
  /** Legacy: filename the route was archived as. Routes are no longer stored, so usually absent. */
  file?: string;
  /** Legacy sequential archive number; absent now that routes aren't stored. */
  number?: number;
  message?: string;
  stats?: { reached?: number; total?: number; loops?: number; by_type?: Record<string, number> };
  /** Full generated route so the client can render its track in the canvas. */
  route?: LayoutProject;
}

/**
 * POST the current canvas (a fab-project export) to the backend hybrid LLM+geometry route
 * pipeline. The backend generates the OHT route and ARCHIVES it as the next numbered JSON in its
 * `LLM routes/` folder (the canvas is not modified). Runs an LLM plan step server-side, so it can
 * take ~30-90s — give it a generous timeout.
 */
export async function generateRouteWithLlm(params: {
  canvas: unknown;
  model?: string;
  startPoint?: { x: number; y: number } | null;
  endPoint?: { x: number; y: number } | null;
  baseUrl?: string;
  timeoutMs?: number;
}): Promise<GenerateRouteResponse> {
  const baseUrl = (params.baseUrl || getFloorPlansApiBase()).replace(/\/$/, '');
  const url = `${baseUrl}/routes/generate`;
  const timeoutMs = params.timeoutMs ?? 180000;
  try {
    const res = await axios.post<GenerateRouteResponse>(
      url,
      {
        canvas: params.canvas,
        model: params.model,
        start_point: params.startPoint ?? null,
        end_point: params.endPoint ?? null,
      },
      { timeout: timeoutMs }
    );
    return res.data;
  } catch (err) {
    throw new Error(`Route generation request failed for ${url}: ${toErrorMessage(err)}`, {
      cause: err,
    });
  }
}

/** A Server-Sent Event from the streaming route generator. */
export interface RouteStreamEvent {
  type:
    | 'status'
    | 'attempt'
    | 'attempt_failed'
    | 'attempt_ok'
    | 'attempts_exhausted'
    | 'done'
    | 'error';
  message?: string;
  n?: number;
  total?: number;
  reason?: string;
  file?: string;
  number?: number;
  route?: LayoutProject;
  stats?: {
    reached?: number;
    total?: number;
    loops?: number;
    stockers?: number;
    by_type?: Record<string, number>;
  };
}

/**
 * Generate a route while STREAMING progress (Server-Sent Events), so the caller can show live
 * status — including LLM plan retry attempts. `onEvent` fires per event; resolves when the stream
 * ends. The `/routes` endpoints aren't auth-gated, but we attach the bearer token if we have one.
 */
export async function generateRouteStream(params: {
  canvas: unknown;
  model?: string;
  startPoint?: { x: number; y: number } | null;
  endPoint?: { x: number; y: number } | null;
  baseUrl?: string;
  onEvent: (ev: RouteStreamEvent) => void;
}): Promise<void> {
  const baseUrl = (params.baseUrl || getFloorPlansApiBase()).replace(/\/$/, '');
  const url = `${baseUrl}/routes/generate/stream`;
  const token = await ensureToken();
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      canvas: params.canvas,
      model: params.model,
      start_point: params.startPoint ?? null,
      end_point: params.endPoint ?? null,
    }),
  });
  if (!resp.ok || !resp.body) {
    throw new Error(`Route stream failed for ${url}: HTTP ${resp.status}`);
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    // SSE frames are separated by a blank line.
    while ((sep = buffer.indexOf('\n\n')) >= 0) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!dataLine) continue;
      const jsonStr = dataLine.slice(5).trim();
      if (!jsonStr) continue;
      try {
        params.onEvent(JSON.parse(jsonStr) as RouteStreamEvent);
      } catch {
        /* ignore malformed frame */
      }
    }
  }
}

/** One message in the assistant conversation (sent back as history for context). */
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** A UI directive the frontend executes after showing the reply. */
export interface ChatAction {
  type: 'none' | 'load_route' | 'request_block_selection' | 'extract_dxf_track';
  payload?: { route?: LayoutProject; layers?: string[] } & Record<string, unknown>;
}

export interface ChatResponse {
  reply: string;
  action: ChatAction;
  data?: Record<string, unknown>;
}

/**
 * POST a chat turn to the fab-planner assistant. The backend interprets the message (Gemini
 * function-calling) and returns a conversational `reply` plus an optional `action` the client runs
 * (load an updated route, or ask the user to mark blocks). `canvas` (marked blocks) and `route`
 * (current track) give the assistant the context it needs to resolve names and edit the route.
 */
export async function sendChatMessage(params: {
  message: string;
  history?: ChatMessage[];
  canvas?: unknown;
  route?: unknown;
  /** Read-only layout, validation and KPI snapshot the assistant answers questions from. */
  layoutSummary?: unknown;
  model?: string;
  baseUrl?: string;
  timeoutMs?: number;
}): Promise<ChatResponse> {
  const baseUrl = (params.baseUrl || getFloorPlansApiBase()).replace(/\/$/, '');
  const url = `${baseUrl}/chat`;
  const timeoutMs = params.timeoutMs ?? 180000;
  try {
    const res = await axios.post<ChatResponse>(
      url,
      {
        message: params.message,
        history: params.history ?? [],
        canvas: params.canvas ?? null,
        route: params.route ?? null,
        layoutSummary: params.layoutSummary ?? null,
        model: params.model,
      },
      { timeout: timeoutMs }
    );
    return res.data;
  } catch (err) {
    throw new Error(`Chat request failed for ${url}: ${toErrorMessage(err)}`, { cause: err });
  }
}
